import React, { useContext } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { TextComponent } from './TextComponent';
import { BulletsComponent } from './BulletsComponent';
import { LogoComponent } from './LogoComponent';
import { ImageComponent } from './ImageComponent';
import { VideoComponent } from './VideoComponent';
import { ShapeComponent } from './ShapeComponent';
import { ComponentWrapper } from './ComponentWrapper';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';

/**
 * Draggable component wrapper that renders editor components with drag-and-drop support.
 * Handles positioning, animations, and media updates for all component types.
 *
 * Gets mode, signDocId, isTemplateMode, templateId, selectedComponentId, and canvasScale from CourseEditorContext.
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique component ID
 * @param {Object} props.component - Component data (type, content, position, styles, etc.)
 * @param {Function} props.onSelect - Callback when component is selected
 * @param {Function} props.onContentChange - Callback when component content changes
 * @param {Function} props.onDelete - Callback to delete component
 * @param {Function} props.onDuplicate - Callback to duplicate component
 * @param {Function} props.onResize - Callback when component is resized
 * @param {Function} props.onVideoComplete - Callback when video playback completes
 * @param {boolean} props.courseCompleted - Whether the course is completed
 * @param {Function} props.onOpenEditorDrawer - Callback to open editor drawer
 * @param {number} props.animationDelay - Delay for component animation (ms)
 * @param {boolean} props.isShowingAIAnimations - Whether AI animations are active
 * @param {Function} props.onAnimationComplete - Callback when animation completes
 * @param {boolean} props.isSequentialAnimationActive - Whether sequential animations are active
 * @param {boolean} props.shouldShowComponent - Whether component should be visible
 */
export const DraggableComponent = ({
    id,
    component,
    onSelect,
    onContentChange,
    onDelete,
    onDuplicate,
    onResize,
    onVideoComplete,
    courseCompleted = false,
    onOpenEditorDrawer,
    animationDelay = 0,
    isShowingAIAnimations = false,
    onAnimationComplete,
    isSequentialAnimationActive = false,
    shouldShowComponent = true
}) => {
    // Get state from context
    const {
        mode = "edit",
        signDocId,
        selectedSection,
        isTemplateMode = false,
        templateId,
        selectedComponentId,
        canvasScale = 1
    } = useContext(CourseEditorContext) || {};

    const sectionId = selectedSection?.id;
    const isEditModeLocal = mode === 'edit';
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id,
        data: {
            component
        },
        // Disable dragging when resizing is in progress
        disabled: window.resizeInProgress || !isEditModeLocal
    });

    // Adjust transform by canvas scale to keep component under mouse cursor during drag
    const adjustedTransform = transform ? {
        x: transform.x / canvasScale,
        y: transform.y / canvasScale
    } : null;

    const style = {
        position: 'absolute',
        left: `${component.x || 0}px`,
        top: `${component.y || 0}px`,
        zIndex: id === selectedComponentId ? 10 : 1,
        transform: adjustedTransform ? `translate3d(${adjustedTransform.x}px, ${adjustedTransform.y}px, 0)` : undefined,
        opacity: isSequentialAnimationActive ? (shouldShowComponent ? 1 : 0) : (isShowingAIAnimations ? 0 : 1),
        animation: isShowingAIAnimations && !isSequentialAnimationActive ? `fadeInComponent 0.5s ease-in-out ${animationDelay}ms forwards` : 'none',
        visibility: isSequentialAnimationActive ? (shouldShowComponent ? 'visible' : 'hidden') : 'visible',
    };

    // Handle content change with optional style properties
    const handleContentChange = (id, content, styleProps) => {
        // If content is undefined, we're just updating style properties
        const newContent = content !== undefined ? content : component.content;

        // Merge the style properties with the component
        onContentChange(id, newContent, styleProps);
    };

    // Allow dragging the component - enabled for all components in edit mode
    // This allows long-press to initiate drag even when not selected
    const wrapperDragProps = (!window.resizeInProgress && isEditModeLocal) ? {
        ...attributes,
        ...listeners
    } : {};

    const onMediaUpdate = ({componentId, newImageId, newVideoId}) => {
        // Update the parent component with the current chapter data
        // This will trigger the translation of media IDs to signed URLs

        // Instead of directly modifying the components array,
        // use the handleContentChange function to update the component's content
        if (newImageId) {
            handleContentChange(componentId, newImageId);
        } else if (newVideoId) {
            handleContentChange(componentId, newVideoId);
        }

        // The update will be handled by the useEffect that watches for component changes
    };

    return (
        <div
            ref={setNodeRef}
            className="editor-component"
            style={style}
        >
            <ComponentWrapper
                id={id}
                component={component}
                isSelected={id === selectedComponentId}
                onSelect={onSelect}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onContentChange={handleContentChange}
                onResize={onResize}
                wrapperDragProps={wrapperDragProps}
                mode={mode}
                onOpenEditorDrawer={onOpenEditorDrawer}
                canvasScale={canvasScale}
            >
                {component.type === 'text' && (
                    <TextComponent
                        id={id}
                        content={component.content}
                        onContentChange={(content, options) => handleContentChange(id, content, options)}
                        isSelected={id === selectedComponentId}
                        onSelect={onSelect}
                        fontSize={component.fontSize}
                        fontWeight={component.bold ? 'bold' : 'normal'}
                        fontStyle={component.italic ? 'italic' : 'normal'}
                        textDecoration={component.underline ? 'underline' : 'none'}
                        color={component.color}
                        borderRadius={component.borderRadius}
                        backgroundColor={component.backgroundColor}
                        opacity={component.opacity || 1}
                        direction={component.direction || 'rtl'}
                        mode={mode}
                        onAnimationComplete={onAnimationComplete}
                        isSequentialAnimationActive={isSequentialAnimationActive}
                    />
                )}
                {component.type === 'bullets' && (
                    <BulletsComponent
                        id={id}
                        content={component.content}
                        onContentChange={(content, options) => handleContentChange(id, content, options)}
                        isSelected={id === selectedComponentId}
                        onSelect={onSelect}
                        fontSize={component.fontSize}
                        fontWeight={component.bold ? 'bold' : 'normal'}
                        fontStyle={component.italic ? 'italic' : 'normal'}
                        textDecoration={component.underline ? 'underline' : 'none'}
                        color={component.color}
                        borderRadius={component.borderRadius}
                        backgroundColor={component.backgroundColor}
                        opacity={component.opacity || 1}
                        direction={component.direction || 'rtl'}
                        mode={mode}
                        onAnimationComplete={onAnimationComplete}
                        isSequentialAnimationActive={isSequentialAnimationActive}
                    />
                )}
                {component.type === 'logo' && (
                    <LogoComponent
                        id={id}
                        isSelected={id === selectedComponentId}
                        onSelect={onSelect}
                        borderRadius={component.borderRadius}
                        backgroundColor={component.backgroundColor}
                        opacity={component.opacity || 1}
                        mode={mode}
                        onAnimationComplete={onAnimationComplete}
                        isSequentialAnimationActive={isSequentialAnimationActive}
                    />
                )}
                {component.type === 'image' && (
                    <ImageComponent
                        id={id}
                        content={component.content}
                        signedUrl={component.signedUrl}
                        isSelected={id === selectedComponentId}
                        onSelect={onSelect}
                        onContentChange={(content) => handleContentChange(id, content)}
                        borderRadius={component.borderRadius}
                        backgroundColor={component.backgroundColor}
                        opacity={component.opacity || 1}
                        mode={mode}
                        signDocId={signDocId}
                        sectionId={sectionId}
                        onCourseUpdate={onMediaUpdate}
                        isTemplateMode={isTemplateMode}
                        templateId={templateId}
                        onAnimationComplete={onAnimationComplete}
                        isSequentialAnimationActive={isSequentialAnimationActive}
                        onOpenEditorDrawer={onOpenEditorDrawer}
                    />
                )}
                {component.type === 'video' && (
                    <VideoComponent
                        id={id}
                        content={component.content}
                        signedUrl={component.signedUrl}
                        isSelected={id === selectedComponentId}
                        onSelect={onSelect}
                        onContentChange={(content) => handleContentChange(id, content)}
                        borderRadius={component.borderRadius}
                        backgroundColor={component.backgroundColor}
                        opacity={component.opacity || 1}
                        mode={mode}
                        signDocId={signDocId}
                        sectionId={sectionId}
                        onCourseUpdate={onMediaUpdate}
                        onVideoComplete={onVideoComplete}
                        courseCompleted={courseCompleted}
                        isTemplateMode={isTemplateMode}
                        templateId={templateId}
                        onAnimationComplete={onAnimationComplete}
                        isSequentialAnimationActive={isSequentialAnimationActive}
                        onOpenEditorDrawer={onOpenEditorDrawer}
                    />
                )}
                {component.type === 'shape' && (
                    <ShapeComponent
                        id={id}
                        content={component.content}
                        onContentChange={(content, options) => handleContentChange(id, content, options)}
                        isSelected={id === selectedComponentId}
                        onSelect={onSelect}
                        borderRadius={component.borderRadius}
                        backgroundColor={component.backgroundColor || '#000000'}
                        opacity={component.opacity || 1}
                        mode={mode}
                        shapeType={component.shapeType || 'rectangle'}
                        borderColor={component.borderColor || '#000000'}
                        borderWidth={component.borderWidth || 0}
                        onAnimationComplete={onAnimationComplete}
                        isSequentialAnimationActive={isSequentialAnimationActive}
                        onOpenEditorDrawer={onOpenEditorDrawer}
                    />
                )}
                {/* Add more component types here as needed */}
            </ComponentWrapper>
        </div>
    );
};
